import { useCallback, type FunctionComponent } from 'react';

import { isMemberLoggedIn, useMemberId } from '@trello/authentication';
import type { RouterLinkProps } from '@trello/router/router-link';
import { useSharedStateSelector } from '@trello/shared-state';
import { token } from '@trello/theme';
import { boardUrlFromShortLink } from '@trello/urls';
import { workspaceState } from '@trello/workspace-state';
import { usePreviousWhileLoading } from '@trello/workspaces';

import { BoardTile } from './BoardTile';
import { useBoardTileLargeFragment } from './BoardTileLargeFragment.generated';
import { useBoardTileStarsFragment } from './BoardTileStarsFragment.generated';

interface BoardListTileConnectedProps extends Omit<RouterLinkProps, 'onClick'> {
  boardId: string;
  onStarClick?: () => void;
  onBoardClick?: () => void;
  showStar?: boolean;
  className?: string;
}

export const BoardListTile: FunctionComponent<BoardListTileConnectedProps> = ({
  boardId,
  onBoardClick,
  onStarClick,
  showStar = true,
  className,
  ...props
}) => {
  const memberId = useMemberId();

  const { data: board } = useBoardTileLargeFragment({
    from: { id: boardId },
    returnPartialData: true,
  });
  const { data: member } = useBoardTileStarsFragment({
    from: { id: memberId },
  });

  const workspace = useSharedStateSelector(
    workspaceState,
    useCallback(
      (state) => ({
        idBoard: state.idBoard,
        isLoading: state.isLoading,
      }),
      [],
    ),
  );
  const idBoard = usePreviousWhileLoading(
    workspace.idBoard,
    workspace.isLoading,
    null,
  );

  const isSelected = boardId === idBoard;

  if (!board) {
    return null;
  }

  const boardStarId =
    member && board.starred
      ? (member.boardStars ?? []).find((star) => star.idBoard === board.id)?.id
      : undefined;

  return (
    <BoardTile
      boardTileClassName={className}
      id={board.id}
      name={board.name}
      // backgroundBrightness={board.prefs?.backgroundBrightness}
      backgroundColor={board.prefs?.backgroundTopColor}
      backgroundImage={board.prefs?.backgroundImage ?? undefined}
      backgroundImageScaled={board.prefs?.backgroundImageScaled ?? undefined}
      backgroundTile={board.prefs?.backgroundTile}
      boardStarId={boardStarId}
      dateLastActivity={
        board.dateLastActivity ? new Date(board.dateLastActivity) : undefined
      }
      dateLastView={
        board.dateLastView ? new Date(board.dateLastView) : undefined
      }
      url={boardUrlFromShortLink(board.shortLink, board.name)}
      onBoardClick={onBoardClick}
      onStarClick={onStarClick}
      // Do not show star button if member is logged out
      isRelatedTemplate={!(showStar && isMemberLoggedIn())}
      isSelected={isSelected}
      isTemplate={board.prefs?.isTemplate}
      starColor={token('color.icon', '#44546F')}
      badgeAppearance="bold"
      {...props}
    />
  );
};
